import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';

interface ShopSettings {
  shopName: string;
  shopAddress: string;
  shopPhone: string;
  shopEmail: string;
  currency: string;
  logo?: string;
}

const SettingsLanguage: React.FC = () => {
  const { currentLanguage, setLanguage, t, languages, isLoading } = useLanguage();
  const [autoTranslate, setAutoTranslate] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [shopSettings, setShopSettings] = useState<ShopSettings>({
    shopName: 'Master Tailor Shop',
    shopAddress: '123 Main Street, Dubai, UAE',
    shopPhone: '+971 50 123 4567',
    shopEmail: 'info@mastertailor.com',
    currency: 'AED'
  });

  // Google Translate integration
  useEffect(() => {
    // Load Google Translate script
    const script = document.createElement('script');
    script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.head.appendChild(script);

    // Initialize Google Translate
    window.googleTranslateElementInit = () => {
      new window.google.translate.TranslateElement({
        pageLanguage: 'en',
        includedLanguages: 'en,hi,pa',
        layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
        autoDisplay: false,
      }, 'google_translate_element');
    };

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleLanguageChange = (languageCode: string) => {
    setLanguage(languageCode);
  };

  const handleAutoTranslateToggle = () => {
    setAutoTranslate(!autoTranslate);
    if (!autoTranslate) {
      // Enable Google Translate
      const translateElement = document.getElementById('google_translate_element');
      if (translateElement) {
        translateElement.style.display = 'block';
      }
    } else {
      // Disable Google Translate
      const translateElement = document.getElementById('google_translate_element');
      if (translateElement) {
        translateElement.style.display = 'none';
      }
    }
  };

  const handleBackup = () => {
    const backupData = {
      settings: shopSettings,
      language: currentLanguage,
      timestamp: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tailor-backup-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleRestore = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const backupData = JSON.parse(e.target?.result as string);
          setShopSettings(backupData.settings);
          setLanguage(backupData.language);
        } catch (_error) {
          alert('Invalid backup file');
        }
      };
      reader.readAsText(file);
    }
  };

  const renderLanguageCard = (language: any) => (
    <div
      key={language.code}
      onClick={() => handleLanguageChange(language.code)}
      className={`bg-white rounded-lg shadow hover:shadow-lg transition-all cursor-pointer border-2 ${
        currentLanguage === language.code ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
      }`}
    >
      <div className="p-6">
        <div className="flex items-center mb-4">
          <div className="text-3xl mr-3">{language.flag}</div>
          <div>
            <h3 className="font-semibold text-gray-900">{language.name}</h3>
            <p className="text-sm text-gray-600">{language.nativeName}</p>
          </div>
        </div>
        {currentLanguage === language.code && (
          <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
            ✓ {t('common.active')}
          </div>
        )}
        {isLoading && currentLanguage === language.code && (
          <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
            {t('common.loading')}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/tailor-dashboard" className="text-gray-400 hover:text-gray-600 mr-4">
                <span className="text-2xl">←</span>
              </Link>
              <div className="flex items-center justify-center w-10 h-10 bg-blue-600 rounded-lg">
                <span className="text-white text-lg font-bold">VT</span>
              </div>
              <span className="ml-3 text-xl font-bold text-gray-900">{t('dashboard.settings')} & Language</span>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="text-2xl">⚙️</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Language Settings */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <span className="mr-2">🌍</span>
              {t('settings.language_settings')}
            </h2>
            <p className="text-sm text-gray-600">{t('settings.choose_language')}</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {languages.map(renderLanguageCard)}
            </div>
            
            {/* Google Translate Integration */}
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900">{t('settings.google_translate')}</h3>
                  <p className="text-sm text-gray-600">{t('settings.auto_translate')}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoTranslate}
                    onChange={handleAutoTranslateToggle}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              
              {autoTranslate && (
                <div id="google_translate_element" className="mt-4">
                  {/* Google Translate widget will be inserted here */}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Shop Settings */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <span className="mr-2">🏪</span>
              {t('settings.shop_information')}
            </h2>
            <p className="text-sm text-gray-600">{t('settings.customize_shop')}</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Shop Name</label>
                <input
                  type="text"
                  value={shopSettings.shopName}
                  onChange={(e) => setShopSettings({...shopSettings, shopName: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={shopSettings.shopPhone}
                  onChange={(e) => setShopSettings({...shopSettings, shopPhone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  value={shopSettings.shopEmail}
                  onChange={(e) => setShopSettings({...shopSettings, shopEmail: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                <select
                  value={shopSettings.currency}
                  onChange={(e) => setShopSettings({...shopSettings, currency: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="AED">AED (UAE Dirham)</option>
                  <option value="USD">USD (US Dollar)</option>
                  <option value="INR">INR (Indian Rupee)</option>
                  <option value="EUR">EUR (Euro)</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Shop Address</label>
                <textarea
                  value={shopSettings.shopAddress}
                  onChange={(e) => setShopSettings({...shopSettings, shopAddress: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Backup & Restore */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <span className="mr-2">💾</span>
              {t('settings.backup_restore')}
            </h2>
            <p className="text-sm text-gray-600">{t('settings.save_restore')}</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center">
                <div className="text-4xl mb-4">📤</div>
                <h3 className="font-semibold text-gray-900 mb-2">Backup Settings</h3>
                <p className="text-sm text-gray-600 mb-4">Download all your settings and data</p>
                <button
                  onClick={handleBackup}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Create Backup
                </button>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-4">📥</div>
                <h3 className="font-semibold text-gray-900 mb-2">Restore Settings</h3>
                <p className="text-sm text-gray-600 mb-4">Upload a backup file to restore settings</p>
                <label className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors cursor-pointer">
                  Choose File
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleRestore}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Settings */}
        {showAdvancedSettings && (
          <div className="bg-white rounded-lg shadow mb-8">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <span className="mr-2">🔧</span>
                Advanced Settings
              </h2>
              <p className="text-sm text-gray-600">Advanced configuration options</p>
            </div>
            <div className="p-6">
              <div className="space-y-6">
                <div>
                  <h3 className="font-medium text-gray-900 mb-4">Data Management</h3>
                  <div className="space-y-3">
                    <button className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">Clear All Data</h4>
                          <p className="text-sm text-gray-600">Remove all customers, orders, and settings</p>
                        </div>
                        <span className="text-red-600">⚠️</span>
                      </div>
                    </button>
                    <button className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">Export All Data</h4>
                          <p className="text-sm text-gray-600">Export customers, orders, and measurements</p>
                        </div>
                        <span className="text-blue-600">📊</span>
                      </div>
                    </button>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-medium text-gray-900 mb-4">Display Settings</h3>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-3" defaultChecked />
                      <span className="text-sm text-gray-700">Show measurement units in inches</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-3" defaultChecked />
                      <span className="text-sm text-gray-700">Enable dark mode</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-3" />
                      <span className="text-sm text-gray-700">Show customer photos</span>
                    </label>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-medium text-gray-900 mb-4">Notification Settings</h3>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-3" defaultChecked />
                      <span className="text-sm text-gray-700">Order due date reminders</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-3" defaultChecked />
                      <span className="text-sm text-gray-700">Pickup notifications</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-3" />
                      <span className="text-sm text-gray-700">Weekly reports</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Language Tips */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <span className="mr-2">💡</span>
              Language Tips
            </h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Supported Languages</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• English - Full support</li>
                  <li>• Hindi - Complete interface</li>
                  <li>• Punjabi - Complete interface</li>
                  <li>• Google Translate - 100+ languages</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Translation Features</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Automatic language detection</li>
                  <li>• Customer-facing translations</li>
                  <li>• Measurement labels in local language</li>
                  <li>• Receipt and invoice translations</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsLanguage; 